import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/lib/AuthContext";
import { bsLabel } from "@/lib/bsDate";
import { ageingBucketColor } from "@/lib/statusStyles";
import { API } from "@/lib/api";
import { authImg } from "@/lib/authImg";
import { ChevronLeft, UserCheck, CheckCircle2, PhoneCall, PackagePlus, ShieldCheck, Wrench, Printer, Share2, AlertTriangle, ImageOff, Truck } from "lucide-react";

export default function ComplaintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [techs, setTechs] = useState([]);
  const [charges, setCharges] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/complaints/${id}`);
    setData(data);
  };

  useEffect(() => {
    load();
    api.get("/meta/enums").then((r) => setStatuses(r.data.complaint_statuses));
    api.get("/technicians").then((r) => setTechs(r.data));
    api.get("/service-charges").then((r) => setCharges(r.data));
    // eslint-disable-next-line
  }, [id]);

  if (!data) return <div className="text-sm text-slate-500">Loading...</div>;
  const c = data.complaint;
  const audits = data.audit_logs;
  const role = user?.role;
  const isServiceHead = role === "service_head";
  const isCCR = role === "ccr" || isServiceHead;
  const isManager = role === "manager" || isServiceHead;
  const isTech = role === "technician" || isServiceHead;
  const isFinalClosed = !!(c && (c.final_closed_at || (c.status === "Closed" && c.final_closed_by)));

  return (
    <div className="space-y-5" data-testid="complaint-detail-page">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate("/complaints")} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-1" data-testid="back-btn">
            <ChevronLeft size={12} /> Back to complaints
          </button>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-bold text-slate-900 font-mono">{c.complaint_number}</h1>
            <StatusBadge status={c.status} testId="detail-status-badge" />
            <span className={`text-xs font-mono ${ageingBucketColor(c.ageing_days)}`}>
              {c.ageing_days != null ? `${c.ageing_days}d ageing` : ""}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Registered {c.registered_date_ad} {c.registered_date_ad && <span className="text-slate-400 ml-1">(BS {bsLabel(c.registered_date_ad)})</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isFinalClosed && isManager && <AssignTechDialog complaintId={c.id} techs={techs} onDone={load} province={c.province} area={c.district_area} />}
          {!isFinalClosed && isTech && <VisitUpdateDialog complaintId={c.id} onDone={load} initial={c} />}
          {!isFinalClosed && isTech && <ConfirmReceivedButton c={c} onDone={load} />}
          {!isFinalClosed && isServiceHead && c.status === "Parts Pending" && <ApprovePartsButton complaintId={c.id} onDone={load} />}
          {!isFinalClosed && isCCR && <HappyCallDialog complaintId={c.id} onDone={load} initial={c} />}
          <JobSheetButton complaintId={c.id} complaintNumber={c.complaint_number} />
          <TrackLinkButton complaintNumber={c.complaint_number} />
          {!isFinalClosed && <StatusChangeDialog complaintId={c.id} statuses={statuses} onDone={load} initial={c} />}
        </div>
      </div>

      {isFinalClosed && (
        <div className="rounded-md border border-slate-300 bg-slate-50 px-4 py-3 flex items-center justify-between"
             data-testid="final-closed-banner">
          <div>
            <div className="text-sm font-semibold text-slate-900">Final Closed — Read Only</div>
            <div className="text-xs text-slate-600">
              Closed by <span className="font-medium">{c.final_closed_by || "—"}</span>
              {c.final_closed_at && <span> on {String(c.final_closed_at).split("T")[0]}</span>}.
              {c.payment_status && <span className="ml-2">Payment status: <span className="font-medium">{c.payment_status}</span>.</span>}
              {" "}This complaint cannot be edited. Reports, exports and the job sheet remain available.
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline ({audits.length})</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">Payment & CSAT</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <DuplicateBanner c={c} onResolved={load} />
          <ClosurePipelinePanel c={c} onChange={load} />
          <PartsWorkflowPanel c={c} />
          <VisitHistoryPanel c={c} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <DetailCard title="Customer">
              <Row label="Name" value={c.customer_name} />
              <Row label="Phone 1" value={c.phone_number_1} mono />
              <Row label="Phone 2" value={c.phone_number_2} mono />
              <Row label="Province" value={c.province} />
              <Row label="District / Area" value={c.district_area} />
              <Row label="Address" value={c.customer_address} />
              <Row label="Dealer" value={c.dealer_name} />
            </DetailCard>

            <DetailCard title="Registration & Product Location">
              <Row label="Registered By" value={REGISTERED_BY_LABELS[c.registered_by_source] || c.registered_by_source} />
              <Row label="Dealer Contact Person" value={c.dealer_contact_person} />
              <Row label="Dealer Phone" value={c.dealer_phone} mono />
              <div className="border-t my-2"></div>
              <Row label="Product Location" value={LOC_TYPE_LABELS[c.product_location_type] || c.product_location_type} />
              <Row label="Location Address" value={c.product_location_address} />
              <Row label="Location Area" value={c.product_location_area} />
              <Row label="Location District" value={c.product_location_district} />
              <Row label="Location Province" value={c.product_location_province} />
              {(c.product_location_latitude != null || c.product_location_longitude != null) && (
                <Row label="GPS" value={`${c.product_location_latitude ?? "—"}, ${c.product_location_longitude ?? "—"}`} mono />
              )}
              <Row label="Location Remarks" value={c.product_location_remarks} />
            </DetailCard>

            <DetailCard title="Product & Warranty">
              <Row label="Brand" value={c.brand} />
              <Row label="Product" value={c.product_name} />
              <Row label="Model" value={c.product_model_number} mono />
              <Row label="Product Category" value={c.product_category} />
              <Row label="Product Size" value={c.product_size} />
              <Row label="Specifications" value={c.product_specifications} />
              <Row label="Other Model" value={c.other_model_number} mono />
              <Row label="Serial 1" value={c.serial_number_1} mono />
              <Row label="Serial 2" value={c.serial_number_2} mono />
              <Row label="Date of Purchase" value={c.date_of_purchase_ad} />
              <Row label="Warranty" value={c.warranty_status} />
              <Row label="In-Warranty Self Damage" value={c.in_warranty_self_damage ? "Yes" : "No"} />
              <Row label="Service Type" value={c.service_type} />
              <div className="border-t my-2"></div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Proof of Purchase</div>
              <ProofPreview label="Warranty Card" url={c.warranty_card_url} testId="proof-warranty-card" />
              <ProofPreview label="Purchase Bill" url={c.purchase_bill_url} testId="proof-purchase-bill" />
            </DetailCard>

            <DetailCard title="Complaint & Technician">
              <Row label="Nature" value={c.nature_of_complaint} />
              <Row label="Additional Remarks" value={c.additional_remarks} />
              <Row label="Details for Tech" value={c.details_for_technician} />
              <div className="border-t my-2"></div>
              <Row label="Technician" value={c.technician_name} />
              <Row label="First Visit Date" value={c.technician_first_visit_date} />
              <Row label="Visit→First Ageing" value={c.first_visit_ageing_days != null ? `${c.first_visit_ageing_days} days` : null} mono />
              <Row label="Technician Remarks" value={c.technician_remarks} />
            </DetailCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DetailCard title="Parts">
              <Row label="Parts Required" value={c.parts_required} />
              <Row label="PNA Part Details" value={c.pna_part_details} />
              <Row label="Approved By" value={c.parts_approved_by} />
              <Row label="Approved Date" value={c.parts_approved_date} />
              <Row label="Sent Date" value={c.parts_sent_date} />
              <Row label="Received by Tech" value={c.received_by_technician_date} />
            </DetailCard>
            <DetailCard title="Closure">
              <Row label="Final Remarks" value={c.final_remarks} />
              <Row label="Closing Date" value={c.closing_date} />
              <Row label="End-to-End Ageing" value={c.end_to_end_ageing_days != null ? `${c.end_to_end_ageing_days} days` : null} mono />
              <Row label="Happy Call Date" value={c.happy_call_date} />
              <Row label="Happy Call Result" value={c.happy_call_result} />
              <Row label="CSAT" value={c.csat_rating ? `${c.csat_rating} ★` : "—"} />
            </DetailCard>
          </div>
          <PODPanel complaintId={c.id} />
        </TabsContent>

        <TabsContent value="timeline">
          <Card><CardContent className="p-5">
            <div className="space-y-1">
              {audits.length === 0 && <div className="text-sm text-slate-500">No audit entries.</div>}
              {audits.map((a) => (
                <div key={a.id} className="timeline-item" data-testid={`audit-${a.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{a.action}</div>
                      {(a.old_status || a.new_status) && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {a.old_status && <span>{a.old_status} → </span>}
                          {a.new_status && <span className="text-slate-900 font-medium">{a.new_status}</span>}
                        </div>
                      )}
                      {a.remarks && <div className="text-xs text-slate-600 mt-1">{a.remarks}</div>}
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <div>{new Date(a.timestamp).toLocaleString()}</div>
                      <div className="text-slate-400">by {a.user_name || "—"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="payment">
          <PaymentForm complaint={c} charges={charges} onDone={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailCard({ title, children }) {
  return (
    <Card><CardContent className="p-5">
      <div className="font-heading font-semibold text-slate-900 mb-3">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </CardContent></Card>
  );
}
function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-baseline text-sm">
      <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold col-span-1">{label}</div>
      <div className={`col-span-2 text-slate-900 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

// Human labels for the new registration/location enums.
const REGISTERED_BY_LABELS = {
  customer_direct: "Customer directly",
  dealer_on_behalf: "Dealer on behalf of customer",
  company_internal: "Company / internal staff",
  other: "Other",
};
const LOC_TYPE_LABELS = {
  customer_place: "Customer place",
  dealer_shop: "Dealer shop",
  dealer_warehouse: "Dealer warehouse",
  other: "Other",
};

const DUP_ACTIONS = [
  { value: "keep_current_close_old", label: "Keep current complaint · close OLD as duplicate" },
  { value: "keep_old_close_current", label: "Keep old complaint · close CURRENT as duplicate" },
  { value: "merge_reopen_old", label: "Merge notes · reopen OLD and close current" },
  { value: "not_duplicate", label: "Mark as NOT a duplicate (clear flag)" },
];

function DuplicateBanner({ c, onResolved }) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [action, setAction] = React.useState("keep_current_close_old");
  const [remarks, setRemarks] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const isUnderReview = c.status === "Duplicate Review Required"
    || (c.duplicate_of_complaint_id && !c.duplicate_resolved_at);
  // Authorised resolvers: Service Manager / Admin (and anyone else with complaints.edit)
  const canResolve = ["admin", "manager", "service_head"].includes(user?.role);
  if (!isUnderReview && !c.duplicate_of_complaint_number) return null;
  const submit = async () => {
    if (!remarks.trim()) { toast.error("Resolution remarks are mandatory"); return; }
    setBusy(true);
    try {
      await api.post(`/complaints/${c.id}/resolve-duplicate`, { action, remarks });
      toast.success("Duplicate resolved");
      setOpen(false); setRemarks("");
      onResolved && onResolved();
    } catch (e) {
      toast.error("Could not resolve", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start justify-between gap-4"
         data-testid="duplicate-banner">
      <div className="min-w-0">
        <div className="font-heading font-semibold text-amber-900 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-700" />
          {isUnderReview ? "Duplicate Review Required" : "Duplicate already resolved"}
        </div>
        <div className="text-sm text-amber-800 mt-1">
          {c.duplicate_warning ||
            "This complaint has a serial-number match with another complaint."}
        </div>
        {c.duplicate_of_complaint_id && (
          <div className="text-sm mt-1">
            Linked complaint:
            <Link to={`/complaints/${c.duplicate_of_complaint_id}`} className="ml-1 font-mono font-semibold hover:underline"
                  data-testid="duplicate-linked-complaint">
              {c.duplicate_of_complaint_number}
            </Link>
          </div>
        )}
        {c.duplicate_resolved_at && (
          <div className="text-xs text-amber-700 mt-1">
            Resolved {c.duplicate_resolved_at.slice(0, 19).replace("T", " ")} by {c.duplicate_resolved_by} · {c.duplicate_resolution}
          </div>
        )}
      </div>
      {isUnderReview && canResolve && (
        <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700 shrink-0"
                onClick={() => setOpen(true)} data-testid="resolve-duplicate-btn">
          Resolve Duplicate
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Duplicate · {c.complaint_number}</DialogTitle>
            <DialogDescription>
              Pick how to handle the duplicate. Remarks are mandatory and audited.
              Original registration dates of both complaints are preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Action</Label>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                      value={action} onChange={(e) => setAction(e.target.value)} data-testid="duplicate-action-select">
                {DUP_ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Resolution Remarks <span className="text-rose-600">*</span>
              </Label>
              <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                        data-testid="duplicate-remarks-input"
                        placeholder="Explain why this resolution was chosen (audited)." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-600 text-white hover:bg-amber-700"
                    data-testid="submit-resolve-duplicate">
              {busy ? "Resolving..." : "Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PartsWorkflowPanel({ c }) {
  // "Parts Requested" (from latest visit + complaint root) side-by-side with
  // "Parts Received" (dispatches with POD + receipt). No new pages, no hidden info.
  const [pods, setPods] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  useEffect(() => {
    api.get(`/spares/pod/by-complaint/${c.id}`).then((r) => setPods(r.data || [])).catch(() => {});
    // Stock-ledger dispatch rows give us part receipt summary; technicians/spares may not all
    // have access — fall back gracefully if 403.
    api.get(`/spares/stock-ledger`, { params: { kind: "dispatch", limit: 50 } })
      .then((r) => setDispatches((r.data || []).filter((d) => d.ref?.complaint_id === c.id)))
      .catch(() => setDispatches([]));
  }, [c.id]);
  const req = c.parts_request || null;
  if (!req && pods.length === 0 && dispatches.length === 0) return null;

  const renderDispatchStatus = (pod) => {
    const dispatched = pod.status === "Dispatched" || !!pod.dispatch_id;
    const received = (c.status === "Received by Technician") && dispatched;
    if (received) return <span className="text-emerald-700 text-xs">Received</span>;
    if (dispatched) return <span className="text-amber-700 text-xs">Pending Receipt</span>;
    return <span className="text-slate-500 text-xs">POD generated · awaiting dispatch</span>;
  };

  return (
    <Card data-testid="parts-workflow-panel"><CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-semibold text-slate-900">Parts Workflow</div>
        <div className="text-xs text-slate-500">Requested ↔ Approved ↔ Dispatched ↔ Received</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT: Parts Requested */}
        <div className="border border-slate-200 rounded-md p-3" data-testid="parts-requested-card">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">Parts Requested</div>
          {!req && <div className="text-sm text-slate-400">No parts requested yet.</div>}
          {req && (
            <div className="space-y-2 text-sm">
              <Row label="Description" value={req.description} />
              <Row label="Part Name" value={req.part_name} />
              <Row label="Part Code" value={req.part_code} mono />
              <Row label="Quantity" value={req.quantity} />
              <Row label="Urgency" value={req.urgency} />
              <Row label="Requested By" value={req.requested_by_name} />
              <Row label="Visit #" value={req.visit_number} />
              {(req.damaged_spare_photo_url || c.damaged_spare_photo_url) && (
                <div className="pt-2">
                  <div className="text-xs text-slate-500 mb-1">Damaged Spare Photo</div>
                  <PhotoThumb url={req.damaged_spare_photo_url || c.damaged_spare_photo_url}
                              alt="damaged spare" testId="damaged-spare-photo" size="lg" />
                </div>
              )}
            </div>
          )}
        </div>
        {/* RIGHT: Parts Received (PODs / Dispatches) */}
        <div className="border border-slate-200 rounded-md p-3" data-testid="parts-received-card">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">Parts Received</div>
          {pods.length === 0 && <div className="text-sm text-slate-400">No parts dispatched yet.</div>}
          {pods.map((p) => (
            <div key={p.id} className="border-b border-slate-100 last:border-b-0 py-2 text-sm" data-testid={`recv-${p.pod_number}`}>
              <div className="flex items-center justify-between">
                <div className="font-mono font-semibold">{p.pod_number}</div>
                {renderDispatchStatus(p)}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                {p.part_name}{p.part_code && <span className="font-mono ml-1">({p.part_code})</span>} · Qty {p.quantity}
              </div>
              {(p.courier_company || p.docket_number) && (
                <div className="text-xs text-slate-500">{p.courier_company || "—"}{p.docket_number && <> · <span className="font-mono">{p.docket_number}</span></>}</div>
              )}
              {p.dispatched_at_final && (
                <div className="text-xs text-slate-500">Dispatched {p.dispatched_at_final.slice(0,19).replace("T"," ")}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </CardContent></Card>
  );
}

function VisitHistoryPanel({ c }) {
  const visits = c.technician_visits || [];
  if (visits.length === 0) return null;
  const totalDistance = visits.reduce((sum, v) => sum + (Number(v.distance_km) || 0), 0);
  return (
    <Card data-testid="visit-history-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading font-semibold text-slate-900">Visit History ({visits.length})</div>
          <div className="text-xs text-slate-600">
            Total Distance: <span className="font-mono font-semibold text-slate-900" data-testid="visit-history-total-distance">{totalDistance.toFixed(1)} km</span>
          </div>
        </div>
        <div className="space-y-2">
          {visits.map((v) => (
            <div key={v.id || v.visit_number} className="border border-slate-200 rounded-md p-3 text-sm"
                 data-testid={`visit-row-${v.visit_number}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-slate-800">
                  Visit #{v.visit_number}
                  <span className="text-xs text-slate-500 ml-2 font-normal">{v.visit_date}</span>
                  {v.visit_status && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                      {v.visit_status}
                    </span>
                  )}
                </div>
                <div className="text-xs text-right">
                  {v.distance_km != null && (
                    <span className="font-mono text-slate-700" data-testid={`visit-distance-${v.visit_number}`}>
                      {Number(v.distance_km).toFixed(1)} km
                    </span>
                  )}
                  {(v.latitude || v.longitude) && (
                    <div className="text-[10px] text-slate-500 font-mono">
                      {v.latitude?.toFixed(4)}, {v.longitude?.toFixed(4)}
                    </div>
                  )}
                </div>
              </div>
              {v.technician_remarks && (
                <div className="mt-1 text-slate-700 whitespace-pre-wrap text-xs">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Remarks: </span>
                  {v.technician_remarks}
                </div>
              )}
              {v.diagnosis && (
                <div className="text-xs text-slate-600">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Diagnosis: </span>
                  {v.diagnosis}
                </div>
              )}
              {v.parts_request && (
                <div className="mt-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs"
                     data-testid={`visit-parts-${v.visit_number}`}>
                  <span className="text-amber-700 font-semibold uppercase tracking-wide text-[10px]">Parts Requested · </span>
                  <span>{v.parts_request.description}</span>
                  {v.parts_request.part_code && <span className="font-mono ml-1">({v.parts_request.part_code})</span>}
                  {v.parts_request.quantity && <span className="ml-1">qty {v.parts_request.quantity}</span>}
                  {v.parts_request.urgency && v.parts_request.urgency !== "normal" && (
                    <span className="ml-1 px-1 py-0 bg-amber-100 text-amber-800 uppercase font-semibold rounded text-[10px]">
                      {v.parts_request.urgency}
                    </span>
                  )}
                </div>
              )}
              {v.distance_remarks && (
                <div className="text-[10px] text-slate-500 italic mt-1">Distance note: {v.distance_remarks}</div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


function ClosurePipelinePanel({ c, onChange }) {
  const { user } = useAuth();
  const [smOpen, setSmOpen] = useState(false);
  const visits = c.technician_visits || [];
  const totalDistance = visits.reduce((sum, v) => sum + (Number(v.distance_km) || 0), 0);
  const [smForm, setSmForm] = useState({
    action: "approve", remarks: "",
    payment_amount: "", technician_self_purchase: "",
    tada_rate: "", tada_adjustment: "0", tada_remarks: "",
  });
  // Reset TADA defaults whenever the dialog reopens for a new complaint
  useEffect(() => {
    if (smOpen) {
      setSmForm((f) => ({
        ...f,
        payment_amount: c.payment_amount != null ? String(c.payment_amount) : "",
        technician_self_purchase: c.technician_self_purchase != null ? String(c.technician_self_purchase) : "",
        tada_rate: c.tada_rate != null ? String(c.tada_rate) : "",
        tada_adjustment: c.tada_adjustment != null ? String(c.tada_adjustment) : "0",
        tada_remarks: c.tada_remarks || "",
      }));
    }
  }, [smOpen, c]);
  const tadaCalculated = (Number(smForm.tada_rate) || 0) * totalDistance;
  const tadaVerified = tadaCalculated + (Number(smForm.tada_adjustment) || 0);
  const [hcOpen, setHcOpen] = useState(false);
  const [hcForm, setHcForm] = useState({ happy_call_result: "Satisfied", csat_rating: 5, remarks: "" });
  const [fcOpen, setFcOpen] = useState(false);
  const [fcForm, setFcForm] = useState({ final_remarks: "" });
  const [busy, setBusy] = useState(false);

  const isSmVerifyStage = c.status === "Request to Close - Pending Service Manager Verification";
  const isCcrStage = c.status === "Pending CCR Happy Call" || c.status === "Customer Not Satisfied / Reopened";
  const isFinalStage = c.status === "Pending Service Head Closure";
  const isAlreadyClosed = c.status === "Closed";

  const canSm = ["admin", "manager"].includes(user?.role);
  const canCcr = ["admin", "ccr", "service_head"].includes(user?.role);
  const canFc = ["admin", "service_head"].includes(user?.role);

  if (!isSmVerifyStage && !isCcrStage && !isFinalStage && !isAlreadyClosed) return null;
  if (!user) return null;

  const post = async (path, body, label, dialogSetter) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success(label);
      dialogSetter && dialogSetter(false);
      onChange && onChange();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="closure-pipeline-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading font-semibold text-slate-900">Closure Pipeline</div>
          <StatusBadge status={c.status} />
        </div>
        <PipelineSteps status={c.status} />
        <div className="mt-3 flex flex-wrap gap-2">
          {isSmVerifyStage && canSm && (
            <Button onClick={() => setSmOpen(true)} className="bg-indigo-600 text-white hover:bg-indigo-700"
                    data-testid="sm-verify-btn">Verify Closure (Service Manager)</Button>
          )}
          {isCcrStage && canCcr && (
            <Button onClick={() => setHcOpen(true)} className="bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="ccr-happy-call-btn">Record Happy Call (CCR)</Button>
          )}
          {isFinalStage && canFc && (
            <Button onClick={() => setFcOpen(true)} className="bg-rose-700 text-white hover:bg-rose-800"
                    data-testid="final-close-btn">Final Close (Service Head)</Button>
          )}
          {isSmVerifyStage && !canSm && <div className="text-xs text-slate-500">Awaiting Service Manager verification.</div>}
          {isCcrStage && !canCcr && <div className="text-xs text-slate-500">Awaiting CCR happy call.</div>}
          {isFinalStage && !canFc && <div className="text-xs text-slate-500">Awaiting Service Head final closure.</div>}
        </div>

        {/* SM dialog */}
        <Dialog open={smOpen} onOpenChange={setSmOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="sm-verify-dialog">
            <DialogHeader className="px-5 pt-5 pb-3 border-b shrink-0">
              <DialogTitle>Service Manager Verification</DialogTitle>
              <DialogDescription>Approve → CCR Happy Call. Reject → back to technician. Remarks mandatory.</DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3 text-sm" data-testid="sm-verify-body">
              {/* Closure request summary submitted by technician */}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="sm-closure-request-summary">
                <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold mb-2">
                  Technician Closure Request
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Final Remarks</div>
                    <div className="text-slate-800 whitespace-pre-wrap" data-testid="sm-tech-final-remarks">
                      {c.final_technician_remarks || c.technician_remarks || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Work Completed</div>
                    <div className="text-slate-800" data-testid="sm-tech-work-completed">
                      {c.work_completed === true ? "Yes" : c.work_completed === false ? "No" : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Service Performed</div>
                    <div className="text-slate-800" data-testid="sm-tech-service-performed">{c.service_performed || "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Parts Used / Returned Notes</div>
                    <div className="text-slate-800 whitespace-pre-wrap" data-testid="sm-tech-parts-notes">{c.parts_used_notes || "—"}</div>
                  </div>
                </div>
              </div>

              {/* Bug 4 (Iter46) — Visit evidence: serial + damaged + service photos, per visit */}
              <SmVisitEvidence visits={visits} complaint={c} />

              <Label className="text-xs">Action</Label>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                      value={smForm.action} onChange={(e) => setSmForm({ ...smForm, action: e.target.value })}
                      data-testid="sm-action-select">
                <option value="approve">Approve & forward to CCR</option>
                <option value="reject">Reject (back to technician)</option>
                <option value="request_correction">Request correction</option>
              </select>
              <Label className="text-xs">Remarks <span className="text-rose-600">*</span></Label>
              <Textarea rows={2} value={smForm.remarks} onChange={(e) => setSmForm({ ...smForm, remarks: e.target.value })}
                        data-testid="sm-remarks-input" />
              {smForm.action === "approve" && (
                <div className="space-y-3">
                  {/* Visit-wise distance breakdown (read-only) */}
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="sm-distance-breakdown">
                    <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold mb-1">
                      Technician Distance (per visit)
                    </div>
                    {visits.length === 0 && <div className="text-xs text-slate-400">No visits logged.</div>}
                    {visits.map((v) => (
                      <div key={v.id || v.visit_number} className="flex justify-between text-xs py-0.5"
                           data-testid={`sm-visit-distance-${v.visit_number}`}>
                        <span className="text-slate-600">
                          Visit #{v.visit_number} · {v.visit_date}
                          {v.distance_remarks && <span className="text-slate-400 ml-1">({v.distance_remarks})</span>}
                        </span>
                        <span className="font-mono text-slate-800">{Number(v.distance_km || 0).toFixed(1)} km</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-slate-200 mt-1 pt-1 text-xs">
                      <span className="font-semibold text-slate-700">Total Distance</span>
                      <span className="font-mono font-bold text-slate-900" data-testid="sm-total-distance">
                        {totalDistance.toFixed(1)} km
                      </span>
                    </div>
                  </div>

                  {/* TADA computation */}
                  <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-2">
                    <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold mb-2">
                      TADA Verification
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">TADA Rate (NPR/km)</Label>
                        <Input type="number" step="0.5" value={smForm.tada_rate}
                               onChange={(e) => setSmForm({ ...smForm, tada_rate: e.target.value })}
                               placeholder="e.g. 6"
                               data-testid="sm-tada-rate-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Calculated TADA</Label>
                        <Input readOnly value={tadaCalculated.toFixed(2)}
                               className="bg-slate-50 font-mono"
                               data-testid="sm-tada-calculated" />
                      </div>
                      <div>
                        <Label className="text-xs">Adjustment (+/-)</Label>
                        <Input type="number" step="1" value={smForm.tada_adjustment}
                               onChange={(e) => setSmForm({ ...smForm, tada_adjustment: e.target.value })}
                               data-testid="sm-tada-adjustment-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Verified TADA (NPR)</Label>
                        <Input readOnly value={tadaVerified.toFixed(2)}
                               className="bg-emerald-50 font-mono font-semibold"
                               data-testid="sm-tada-verified" />
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-xs">TADA Remarks</Label>
                      <Input value={smForm.tada_remarks}
                             onChange={(e) => setSmForm({ ...smForm, tada_remarks: e.target.value })}
                             placeholder="optional · e.g. reason for adjustment"
                             data-testid="sm-tada-remarks-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Payment (NPR)</Label>
                      <Input type="number" value={smForm.payment_amount} onChange={(e) => setSmForm({ ...smForm, payment_amount: e.target.value })} data-testid="sm-payment-input" /></div>
                    <div><Label className="text-xs">Self-Purchase (NPR)</Label>
                      <Input type="number" value={smForm.technician_self_purchase} onChange={(e) => setSmForm({ ...smForm, technician_self_purchase: e.target.value })} data-testid="sm-selfpurchase-input" /></div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="px-5 py-3 border-t bg-white shrink-0 gap-2">
              <Button variant="outline" onClick={() => setSmOpen(false)} data-testid="sm-cancel">Cancel</Button>
              <Button disabled={busy} className="bg-indigo-600 text-white hover:bg-indigo-700"
                      onClick={() => post(`/complaints/${c.id}/sm-verify`,
                        { action: smForm.action,
                          remarks: smForm.remarks,
                          payment_amount: smForm.payment_amount || undefined,
                          technician_self_purchase: smForm.technician_self_purchase || undefined,
                          tada_rate: smForm.tada_rate || undefined,
                          tada_total_distance_km: totalDistance || undefined,
                          tada_amount_calculated: smForm.tada_rate ? tadaCalculated : undefined,
                          tada_adjustment: smForm.tada_adjustment || undefined,
                          tada_verified_amount: smForm.tada_rate ? tadaVerified : undefined,
                          tada_charges: smForm.tada_rate ? tadaVerified : undefined,
                          tada_remarks: smForm.tada_remarks || undefined },
                        "Submitted", setSmOpen)}
                      data-testid="sm-submit">{busy ? "Submitting..." :
                        (smForm.action === "approve" ? "Approve & forward to CCR"
                         : smForm.action === "reject" ? "Reject back to Technician"
                         : "Request Correction")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CCR Happy Call dialog */}
        <Dialog open={hcOpen} onOpenChange={setHcOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>CCR Happy Call</DialogTitle>
              <DialogDescription>
                Confirm with customer. Satisfied → forward to Service Head. Not Resolved → reopen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <Label className="text-xs">Result</Label>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                      value={hcForm.happy_call_result} onChange={(e) => setHcForm({ ...hcForm, happy_call_result: e.target.value })}
                      data-testid="hc-result-select">
                <option>Satisfied</option>
                <option>Not Resolved</option>
                <option>Unreachable</option>
              </select>
              <Label className="text-xs">CSAT (1-5)</Label>
              <Input type="number" min={1} max={5} value={hcForm.csat_rating}
                     onChange={(e) => setHcForm({ ...hcForm, csat_rating: parseInt(e.target.value) || 1 })}
                     data-testid="hc-csat-input" />
              <Label className="text-xs">Remarks</Label>
              <Textarea rows={2} value={hcForm.remarks} onChange={(e) => setHcForm({ ...hcForm, remarks: e.target.value })}
                        data-testid="hc-remarks-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHcOpen(false)}>Cancel</Button>
              <Button disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => post(`/complaints/${c.id}/happy-call`, hcForm, "Happy call recorded", setHcOpen)}
                      data-testid="hc-submit">{busy ? "Submitting..." : "Submit"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Final Close dialog */}
        <Dialog open={fcOpen} onOpenChange={setFcOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Service Head Final Closure</DialogTitle>
              <DialogDescription>
                Closes the complaint after technician, Service Manager, and CCR steps have all completed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <Label className="text-xs">Final Remarks</Label>
              <Textarea rows={3} value={fcForm.final_remarks} onChange={(e) => setFcForm({ ...fcForm, final_remarks: e.target.value })}
                        data-testid="fc-remarks-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFcOpen(false)}>Cancel</Button>
              <Button disabled={busy} className="bg-rose-700 text-white hover:bg-rose-800"
                      onClick={() => post(`/complaints/${c.id}/final-close`, fcForm, "Complaint closed", setFcOpen)}
                      data-testid="fc-submit">{busy ? "Closing..." : "Close Complaint"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const PIPELINE_ORDER = [
  { key: "request_to_close", label: "Tech Requested" },
  { key: "sm_verify", label: "SM Verified" },
  { key: "ccr", label: "CCR Happy Call" },
  { key: "final", label: "Final Closure" },
];

function PipelineSteps({ status }) {
  // Visualize where we are in the closure pipeline (lightweight stepper).
  const idx = (() => {
    if (status === "Request to Close - Pending Service Manager Verification") return 0;
    if (status === "Rejected by Service Manager") return 0;
    if (status === "Pending CCR Happy Call") return 1;
    if (status === "Customer Not Satisfied / Reopened") return 1;
    if (status === "Pending Service Head Closure") return 2;
    if (status === "Closed") return 3;
    return -1;
  })();
  return (
    <ol className="flex items-center gap-2 text-[11px] mt-2 flex-wrap">
      {PIPELINE_ORDER.map((step, i) => (
        <li key={step.key} className={`flex items-center gap-1 ${i <= idx ? "text-slate-900" : "text-slate-400"}`}>
          <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold
                            ${i < idx ? "bg-emerald-500 text-white" : i === idx ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"}`}>
            {i + 1}
          </span>
          <span className="font-semibold">{step.label}</span>
          {i < PIPELINE_ORDER.length - 1 && <span className="text-slate-300">→</span>}
        </li>
      ))}
    </ol>
  );
}

function PODPanel({ complaintId }) {
  const { user } = useAuth();
  const [pods, setPods] = useState([]);
  const load = () => api.get(`/spares/pod/by-complaint/${complaintId}`)
    .then((r) => setPods(r.data || []))
    .catch(() => setPods([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [complaintId]);

  const canUploadBill = ["admin", "spares"].includes(user?.role);

  const downloadPdf = (pn) => downloadBlob({
    url: `/spares/pod/${encodeURIComponent(pn)}/pdf`,
    filename: `${pn}.pdf`,
  });
  const downloadBill = (pod) => {
    // Bill is a data URL; just trigger a download.
    if (!pod.transport_bill_url) return;
    const ext = pod.transport_bill_url.startsWith("data:application/pdf") ? "pdf" : "jpg";
    const a = document.createElement("a");
    a.href = pod.transport_bill_url;
    a.download = `${pod.pod_number}-transport-bill.${ext}`;
    a.click();
  };
  const uploadBill = async (pod, file) => {
    const ok = file.type.startsWith("image/") || file.type === "application/pdf";
    if (!ok) { toast.error("Transport bill must be image or PDF"); return; }
    if (file.size > 6_000_000) { toast.error("Transport bill too large (max ~6 MB)"); return; }
    const url = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onerror = rej; r.onload = () => res(r.result);
      r.readAsDataURL(file);
    });
    try {
      await api.post(`/spares/pod/${pod.pod_number}/transport-bill`, { transport_bill_url: url });
      toast.success("Transport bill uploaded");
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  if (pods.length === 0) return null;
  return (
    <Card data-testid="complaint-pod-panel"><CardContent className="p-5">
      <div className="font-heading font-semibold text-slate-900 mb-3">Proof of Dispatch (POD)</div>
      <div className="space-y-4">
        {pods.map((p) => (
          <div key={p.id} className="border border-slate-200 rounded-md p-3" data-testid={`pod-item-${p.pod_number}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono font-semibold">{p.pod_number}</div>
                <div className="text-xs text-slate-500">
                  {p.part_name} {p.part_code && <span className="font-mono">({p.part_code})</span>} · Qty {p.quantity} · {(p.dispatch_date || "").slice(0, 19).replace("T", " ")}
                </div>
                <div className="mt-1 text-xs text-slate-700 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5">
                  {p.courier_company && <span><b>Courier:</b> {p.courier_company}</span>}
                  {p.docket_number && <span><b>Docket:</b> <span className="font-mono">{p.docket_number}</span></span>}
                  {p.freight_charge != null && <span><b>Freight:</b> NPR {p.freight_charge}</span>}
                  {p.sent_from && <span><b>From:</b> {p.sent_from}</span>}
                  {p.sent_to && <span><b>To:</b> {p.sent_to}</span>}
                  {p.dispatch_date_input && <span><b>Dispatch:</b> {p.dispatch_date_input}</span>}
                  {p.expected_delivery_date && <span><b>ETA:</b> {p.expected_delivery_date}</span>}
                  {p.receiver_name && <span><b>Receiver:</b> {p.receiver_name}</span>}
                </div>
                {p.courier_remarks && <div className="text-xs text-slate-500 mt-1">{p.courier_remarks}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.spare_photo_url && (
                  <PhotoThumb url={p.spare_photo_url} alt="spare"
                              testId={`pod-spare-photo-${p.pod_number}`} size="sm" />
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <Button size="sm" variant="outline" onClick={() => downloadPdf(p.pod_number)} data-testid={`download-pod-${p.pod_number}`}>POD PDF</Button>
              {p.spare_photo_url && (
                <a href={authImg(p.spare_photo_url)} download={`${p.pod_number}-spare.png`}>
                  <Button size="sm" variant="outline" data-testid={`download-spare-photo-${p.pod_number}`}>Spare Photo</Button>
                </a>
              )}
              {p.transport_bill_url && (
                <Button size="sm" variant="outline" onClick={() => downloadBill(p)} data-testid={`download-bill-${p.pod_number}`}>Transport Bill</Button>
              )}
              {!p.transport_bill_url && canUploadBill && (
                <label className="inline-flex items-center gap-1 text-xs text-amber-700 cursor-pointer hover:underline" data-testid={`upload-bill-${p.pod_number}`}>
                  <input type="file" accept="image/*,application/pdf" className="hidden"
                         onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBill(p, f); }} />
                  + Add transport bill
                </label>
              )}
              {!p.transport_bill_url && !canUploadBill && (
                <span className="text-xs text-slate-500">Transport bill pending</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function AssignTechDialog({ complaintId, techs, onDone, province, area }) {
  const [open, setOpen] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [techId, setTechId] = useState("");

  const onOpen = async (o) => {
    setOpen(o);
    if (o) {
      const params = { province: province || "", area: area || "" };
      const { data } = await api.get("/technicians/suggest", { params });
      setSuggested(data);
    }
  };

  const submit = async () => {
    try {
      await api.post(`/complaints/${complaintId}/assign-technician`, { technician_id: techId });
      toast.success("Technician assigned");
      setOpen(false);
      onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="assign-tech-btn"><UserCheck size={14} className="mr-1" /> Assign Technician</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Assign Technician</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Suggested (based on province/area)</Label>
            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
              {suggested.map((t) => (
                <button key={t.id} type="button" onClick={() => setTechId(t.id)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md border ${techId === t.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`suggest-tech-${t.id}`}>
                  <div className="flex justify-between">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-slate-500">match {t.match_score}</div>
                  </div>
                  <div className="text-xs text-slate-500">{t.province} · {t.area} · {t.category}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Or manually pick</Label>
            <Select value={techId} onValueChange={setTechId}>
              <SelectTrigger data-testid="manual-tech-select"><SelectValue placeholder="Choose technician" /></SelectTrigger>
              <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!techId} className="bg-slate-900 text-white" data-testid="confirm-assign-btn">Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VisitUpdateDialog({ complaintId, onDone, initial }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(initial?.technician_first_visit_date || new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState(initial?.technician_remarks || "");

  const submit = async () => {
    try {
      await api.post(`/complaints/${complaintId}/visit-update`, { technician_first_visit_date: date, technician_remarks: remarks });
      toast.success("Visit updated");
      setOpen(false); onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="visit-update-btn"><Wrench size={14} className="mr-1" /> Visit Update</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Update Visit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>First Visit Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="visit-date-input" /></div>
          <div><Label>Technician Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="visit-remarks-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-900 text-white" data-testid="confirm-visit-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartsRequestDialog({ complaintId, onDone }) {
  const [open, setOpen] = useState(false);
  const [parts, setParts] = useState("");
  const [pna, setPna] = useState(false);
  const [remarks, setRemarks] = useState("");

  const submit = async () => {
    try {
      await api.post(`/complaints/${complaintId}/request-parts`, { parts_required: parts, pna, technician_remarks: remarks });
      toast.success(pna ? "PNA case raised" : "Parts requested");
      setOpen(false); onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="parts-request-btn"><PackagePlus size={14} className="mr-1" /> Request Parts</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Request Parts</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Parts Required</Label><Textarea value={parts} onChange={(e) => setParts(e.target.value)} data-testid="parts-required-input" /></div>
          <div><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={pna} onChange={(e) => setPna(e.target.checked)} data-testid="pna-checkbox" />
            <Label>This is a PNA case (Part Not Available - needs market purchase approval)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-900 text-white" data-testid="confirm-parts-btn">Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovePartsButton({ complaintId, onDone }) {
  const submit = async () => {
    try { await api.post(`/complaints/${complaintId}/approve-parts`); toast.success("Parts approved"); onDone(); }
    catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  return <Button variant="outline" onClick={submit} data-testid="approve-parts-btn"><ShieldCheck size={14} className="mr-1" /> Approve Parts</Button>;
}

function ConfirmReceivedButton({ c, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [remarks, setRemarks] = useState("");
  // Only show when there is a pending dispatch for this complaint.
  const pr = c.parts_request || {};
  const pendingReceipt = pr.dispatched_at && !c.received_by_technician_date;
  const inTransitStatus = ["Parts in Transit", "Parts Sent", "Parts Delivered"].includes(c.status);
  if (!(pendingReceipt || inTransitStatus)) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const body = {};
      if (pr.dispatch_id) body.dispatch_id = pr.dispatch_id;
      else if (pr.dispatch_pod_number) body.pod_number = pr.dispatch_pod_number;
      if (remarks.trim()) body.remarks = remarks.trim();
      await api.post(`/complaints/${c.id}/confirm-received`, body);
      toast.success("Receipt confirmed");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="confirm-received-btn"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
        <CheckCircle2 size={14} className="mr-1" /> Confirm Received
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="confirm-received-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Parts Received · {c.complaint_number}</DialogTitle>
            <DialogDescription>
              POD <span className="font-mono">{pr.dispatch_pod_number || "—"}</span>
              {pr.part_name && <> · {pr.part_name}</>}
              {pr.dispatched_qty && <> · qty {pr.dispatched_qty}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <Label className="text-xs">Receipt Remarks (optional)</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Condition / packaging notes" data-testid="confirm-received-remarks" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="confirm-received-submit">{busy ? "Saving…" : "Confirm Receipt"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function JobSheetButton({ complaintId, complaintNumber }) {
  const download = () => downloadBlob({
    url: `/complaints/${complaintId}/job-sheet`,
    filename: `JobSheet-${complaintNumber}.pdf`,
  });
  return <Button variant="outline" onClick={download} data-testid="job-sheet-btn"><Printer size={14} className="mr-1" /> Print Job Sheet</Button>;
}

function TrackLinkButton({ complaintNumber }) {
  const copy = () => {
    const url = `${window.location.origin}/track?complaint_number=${encodeURIComponent(complaintNumber)}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Tracker link copied", { description: url }));
  };
  return <Button variant="outline" onClick={copy} data-testid="share-track-btn"><Share2 size={14} className="mr-1" /> Share Tracker Link</Button>;
}

function HappyCallDialog({ complaintId, onDone, initial }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(initial?.happy_call_result || "");
  const [csat, setCsat] = useState(initial?.csat_rating?.toString() || "");
  const [remarks, setRemarks] = useState("");
  const [date, setDate] = useState(initial?.happy_call_date || new Date().toISOString().slice(0, 10));

  const submit = async () => {
    try {
      await api.post(`/complaints/${complaintId}/happy-call`, { complaint_id: complaintId, happy_call_date: date, happy_call_result: result, csat_rating: csat ? parseInt(csat) : null, remarks });
      toast.success("Happy call recorded"); setOpen(false); onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="happy-call-btn"><PhoneCall size={14} className="mr-1" /> Happy Call</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Happy Call</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Call Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div>
            <Label>Result</Label>
            <Select value={result} onValueChange={setResult}>
              <SelectTrigger data-testid="happy-call-result-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {["Satisfied","Not Satisfied","Unreachable","Will retry"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>CSAT (1-5)</Label>
            <Select value={csat} onValueChange={setCsat}>
              <SelectTrigger data-testid="csat-select"><SelectValue placeholder="Rating" /></SelectTrigger>
              <SelectContent>{[1,2,3,4,5].map((r) => <SelectItem key={r} value={r.toString()}>{r} ★</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!result} className="bg-slate-900 text-white" data-testid="confirm-happy-call-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusChangeDialog({ complaintId, statuses, onDone, initial }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [finalRemarks, setFinalRemarks] = useState(initial?.final_remarks || "");
  // Bug 2 (Iter46) — POD payload for Exchange / Sales Return
  const [pod, setPod] = useState({
    exchange_pod_number: "",
    exchange_dispatch_date: new Date().toISOString().slice(0, 10),
    exchange_courier: "",
    exchange_docket: "",
    exchange_receiver: "",
    exchange_transport_bill_url: "",
    exchange_remarks: "",
  });
  const isPodStatus = status === "Exchange" || status === "Sales Return";
  const podReady = !isPodStatus || (
    pod.exchange_dispatch_date && pod.exchange_courier.trim() &&
    pod.exchange_docket.trim() && pod.exchange_receiver.trim()
  );

  const submit = async () => {
    const extra = {};
    if (status === "Closed") extra.final_remarks = finalRemarks;
    if (isPodStatus) Object.assign(extra, pod);
    try {
      await api.post(`/complaints/${complaintId}/status`, { status, remarks, extra });
      toast.success(`Status updated${isPodStatus ? " with POD" : ""}`);
      setOpen(false); onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="change-status-btn"><CheckCircle2 size={14} className="mr-1" /> Change Status</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Change Status</DialogTitle>
          <DialogDescription>Update this complaint&apos;s state. Exchange / Sales Return require a POD.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>New Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="status-select"><SelectValue placeholder="Choose status" /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
          {status === "Closed" && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-900">
                Closing requires final remarks and a recorded happy call result.
              </div>
              <div><Label>Final Remarks</Label><Textarea value={finalRemarks} onChange={(e) => setFinalRemarks(e.target.value)} data-testid="final-remarks-input" /></div>
            </>
          )}
          {isPodStatus && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="exchange-pod-block">
              <div className="flex items-center gap-2 text-[11px] uppercase font-semibold text-indigo-800">
                <Truck size={12} /> POD Details ({status})
              </div>
              <div className="text-[11px] text-indigo-900">
                Same standard as spares dispatch POD. All fields marked * are mandatory.
                Leave POD number blank to auto-generate.
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <Label className="text-xs">POD Number</Label>
                  <Input placeholder="auto-generate if blank" value={pod.exchange_pod_number}
                         onChange={(e) => setPod({ ...pod, exchange_pod_number: e.target.value })}
                         data-testid="exchange-pod-number" />
                </div>
                <div>
                  <Label className="text-xs">Dispatch / Return Date *</Label>
                  <Input type="date" value={pod.exchange_dispatch_date}
                         onChange={(e) => setPod({ ...pod, exchange_dispatch_date: e.target.value })}
                         data-testid="exchange-pod-date" />
                </div>
                <div>
                  <Label className="text-xs">Courier / Transport *</Label>
                  <Input value={pod.exchange_courier}
                         onChange={(e) => setPod({ ...pod, exchange_courier: e.target.value })}
                         data-testid="exchange-pod-courier" />
                </div>
                <div>
                  <Label className="text-xs">Docket / Tracking # *</Label>
                  <Input value={pod.exchange_docket}
                         onChange={(e) => setPod({ ...pod, exchange_docket: e.target.value })}
                         data-testid="exchange-pod-docket" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Destination / Receiver *</Label>
                  <Input value={pod.exchange_receiver}
                         onChange={(e) => setPod({ ...pod, exchange_receiver: e.target.value })}
                         placeholder="e.g. dealer name or customer address"
                         data-testid="exchange-pod-receiver" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Transport Bill URL (photo/PDF)</Label>
                  <Input value={pod.exchange_transport_bill_url}
                         onChange={(e) => setPod({ ...pod, exchange_transport_bill_url: e.target.value })}
                         placeholder="paste URL after upload"
                         data-testid="exchange-pod-bill-url" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">POD Remarks</Label>
                  <Input value={pod.exchange_remarks}
                         onChange={(e) => setPod({ ...pod, exchange_remarks: e.target.value })}
                         data-testid="exchange-pod-remarks" />
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!status || !podReady} className="bg-slate-900 text-white" data-testid="confirm-status-btn">
            {isPodStatus ? "Save POD & Update" : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentForm({ complaint, charges, onDone }) {
  const [form, setForm] = useState({
    payment_amount: complaint.payment_amount || "",
    distance_above_15km: complaint.distance_above_15km || 0,
    tada_charges: complaint.tada_charges || 0,
    technician_self_purchase: complaint.technician_self_purchase || 0,
    cost_of_product: complaint.cost_of_product || 0,
    final_payment: complaint.final_payment || "",
    technician_payment_remarks: complaint.technician_payment_remarks || "",
  });

  const applyCharge = (id) => {
    const c = charges.find((x) => x.id === id);
    if (c) setForm((f) => ({ ...f, payment_amount: c.price_npr }));
  };

  const recalcTada = () => {
    const km = parseFloat(form.distance_above_15km) || 0;
    setForm((f) => ({ ...f, tada_charges: km * 6 }));
  };

  const submit = async () => {
    try {
      const data = { ...complaint, ...form };
      // ensure all numeric values
      ["payment_amount","distance_above_15km","tada_charges","technician_self_purchase","cost_of_product","final_payment"].forEach((k) => {
        if (data[k] === "" || data[k] == null) data[k] = null;
        else data[k] = parseFloat(data[k]);
      });
      await api.put(`/complaints/${complaint.id}`, data);
      toast.success("Payment updated");
      onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <Card><CardContent className="p-5 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Apply Service Charge</Label>
          <Select onValueChange={applyCharge}>
            <SelectTrigger data-testid="apply-charge-select"><SelectValue placeholder="Pick from price list..." /></SelectTrigger>
            <SelectContent>{charges.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — NPR {c.price_npr}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Payment Amount / Service Cost (NPR)</Label>
          <Input type="number" value={form.payment_amount} onChange={(e) => setForm({ ...form, payment_amount: e.target.value })} data-testid="payment-amount-input" />
        </div>
        <div>
          <Label>Distance above 15 km</Label>
          <div className="flex gap-2">
            <Input type="number" value={form.distance_above_15km} onChange={(e) => setForm({ ...form, distance_above_15km: e.target.value })} />
            <Button type="button" variant="outline" onClick={recalcTada} className="whitespace-nowrap">×6 NPR</Button>
          </div>
        </div>
        <div><Label>TADA Charges (NPR)</Label><Input type="number" value={form.tada_charges} onChange={(e) => setForm({ ...form, tada_charges: e.target.value })} /></div>
        <div><Label>Technician Self Purchase</Label><Input type="number" value={form.technician_self_purchase} onChange={(e) => setForm({ ...form, technician_self_purchase: e.target.value })} /></div>
        <div><Label>Cost of Product</Label><Input type="number" value={form.cost_of_product} onChange={(e) => setForm({ ...form, cost_of_product: e.target.value })} /></div>
        <div><Label>Final Payment</Label><Input type="number" value={form.final_payment} onChange={(e) => setForm({ ...form, final_payment: e.target.value })} data-testid="final-payment-input" /></div>
        <div className="md:col-span-2"><Label>Technician Payment Remarks</Label><Textarea value={form.technician_payment_remarks} onChange={(e) => setForm({ ...form, technician_payment_remarks: e.target.value })} /></div>
      </div>
      <div className="flex justify-end"><Button onClick={submit} className="bg-slate-900 text-white" data-testid="save-payment-btn">Save</Button></div>
    </CardContent></Card>
  );
}


function ProofPreview({ label, url, testId }) {
  if (!url) return (
    <div className="flex items-center justify-between text-xs py-1" data-testid={`${testId}-empty`}>
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-400 italic">Not uploaded</span>
    </div>
  );
  const isImage = url.startsWith("data:image/") || /\.(png|jpg|jpeg|webp|gif)$/i.test(url);
  const isPdf = url.startsWith("data:application/pdf") || /\.pdf$/i.test(url);
  return (
    <div className="flex items-center justify-between gap-2 py-1" data-testid={testId}>
      <span className="text-xs text-slate-500">{label}</span>
      {isImage && (
        <a href={url} target="_blank" rel="noopener noreferrer" download data-testid={`${testId}-link`}>
          <img src={url} alt={label} className="h-10 w-10 object-cover rounded border hover:opacity-80 cursor-pointer" />
        </a>
      )}
      {isPdf && (
        <a href={url} target="_blank" rel="noopener noreferrer" download
           className="text-xs text-sky-700 underline hover:text-sky-900" data-testid={`${testId}-link`}>
          Open PDF
        </a>
      )}
      {!isImage && !isPdf && (
        <a href={url} target="_blank" rel="noopener noreferrer"
           className="text-xs text-sky-700 underline" data-testid={`${testId}-link`}>Open</a>
      )}
    </div>
  );
}


/**
 * PhotoThumb — auth-aware image thumbnail that opens full-size in a new tab.
 * Handles GridFS / protected file URLs by appending JWT via authImg().
 * Falls back to a "Photo not available" pill on 404 / auth failure.
 */
function PhotoThumb({ url, alt = "photo", testId, size = "md", className = "" }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [url]);
  if (!url) return null;
  const sz = size === "lg" ? "h-28 w-28" : size === "sm" ? "h-14 w-14" : "h-20 w-20";
  const full = authImg(url);
  if (broken) {
    return (
      <div className={`${sz} rounded border border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-[10px] text-slate-400 ${className}`}
           data-testid={`${testId || "photo"}-missing`}>
        <ImageOff size={14} className="mb-0.5" />
        Photo not available
      </div>
    );
  }
  return (
    <a href={full} target="_blank" rel="noopener noreferrer"
       className={`inline-block ${className}`} data-testid={testId} title={alt}>
      <img src={full} alt={alt}
           className={`${sz} rounded border border-slate-200 object-cover hover:opacity-90 transition`}
           onError={() => setBroken(true)} />
    </a>
  );
}

/**
 * SmVisitEvidence — Bug 4 (Iter46). Aggregates all visit-wise photos and
 * remarks that a Service Manager needs to inspect BEFORE approving closure.
 * Includes: serial sticker (first visit), damaged spare (per visit),
 * service photos array, and per-visit remarks / diagnosis / distance.
 * All thumbnails open full-size in a new tab via authImg().
 */
function SmVisitEvidence({ visits, complaint }) {
  const c = complaint || {};
  const serialUrl = c.serial_sticker_url ||
    (visits.find((v) => v.serial_sticker_url) || {}).serial_sticker_url;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3" data-testid="sm-visit-evidence">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-700 font-semibold">
        <ShieldCheck size={12} className="text-emerald-600" /> Visit Evidence
      </div>
      {serialUrl && (
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Serial Number Photo</div>
          <PhotoThumb url={serialUrl} alt="serial sticker" testId="sm-serial-photo" size="md" />
        </div>
      )}
      {visits.length === 0 && (
        <div className="text-xs text-slate-400 italic">No visits logged yet.</div>
      )}
      {visits.map((v) => (
        <div key={v.id || v.visit_number} className="border-t border-slate-100 pt-2"
             data-testid={`sm-visit-block-${v.visit_number}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold text-slate-800">
              Visit #{v.visit_number}
              <span className="text-slate-500 font-normal ml-1">· {v.visit_date}</span>
            </div>
            <div className="text-[11px] font-mono text-slate-600">
              {Number(v.distance_km || 0).toFixed(1)} km
            </div>
          </div>
          {(v.diagnosis || v.remarks || v.distance_remarks) && (
            <div className="text-[11px] text-slate-700 space-y-0.5 mb-1.5">
              {v.diagnosis && <div><span className="text-slate-500">Diagnosis:</span> {v.diagnosis}</div>}
              {v.remarks && <div><span className="text-slate-500">Remarks:</span> {v.remarks}</div>}
              {v.distance_remarks && <div><span className="text-slate-500">Distance note:</span> {v.distance_remarks}</div>}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {v.damaged_spare_photo_url && (
              <div>
                <div className="text-[10px] text-rose-600 font-semibold mb-0.5">Damaged Spare</div>
                <PhotoThumb url={v.damaged_spare_photo_url} alt={`v${v.visit_number} damaged`}
                            testId={`sm-visit-${v.visit_number}-damaged`} size="md" />
              </div>
            )}
            {(v.photos || []).map((p, i) => (
              <div key={i}>
                {i === 0 && <div className="text-[10px] text-slate-500 font-semibold mb-0.5">Service Photos</div>}
                {i > 0 && <div className="text-[10px] text-transparent mb-0.5">·</div>}
                <PhotoThumb url={p} alt={`v${v.visit_number} photo ${i + 1}`}
                            testId={`sm-visit-${v.visit_number}-photo-${i}`} size="md" />
              </div>
            ))}
            {!v.damaged_spare_photo_url && (!v.photos || v.photos.length === 0) && (
              <div className="text-[11px] text-slate-400 italic">No photos uploaded on this visit.</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
